import axios, { AxiosError } from 'axios';

/**
 * System Health Service
 *
 * Handles all system health-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// System Health endpoints
const SYSTEM_HEALTH_ENDPOINTS = {
  health: `${API_BASE_URL}/system-health`,
  errors: `${API_BASE_URL}/system-health/errors`,
  errorsTimeSeries: `${API_BASE_URL}/system-health/errors/timeseries`,
  loginsTimeSeries: `${API_BASE_URL}/system-health/logins/timeseries`
};

/**
 * System metrics interface
 */
export interface SystemMetrics {
  uptime: number;
  totalErrors: number;
  totalLogins: number;
  totalApiCalls: number;
  errorRate: number;
  avgResponseTime: number;
}

/**
 * Error log interface
 */
export interface ErrorLog {
  id: string;
  timestamp: string;
  level: 'error' | 'warning' | 'critical' | 'info';
  message: string;
  stack?: string | null;
  endpoint?: string | null;
  method?: string | null;
  status_code?: number | null;
  user_id?: string | null;
  company_id?: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
}

/**
 * Time series data point
 */
export interface TimeSeriesData {
  timestamp: string;
  count?: number;
  value?: number;
}

/**
 * API Response interfaces
 */
export interface SystemHealthResponse {
  success: boolean;
  data: {
    metrics: SystemMetrics;
    time_range: string;
  };
}

export interface ErrorLogsResponse {
  success: boolean;
  data: ErrorLog[];
}

export interface TimeSeriesResponse {
  success: boolean;
  data: TimeSeriesData[];
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Get system health metrics
 */
export async function getSystemHealth(
  timeRange: '24h' | '7d' | '30d',
  token: string
): Promise<SystemHealthResponse['data']> {
  try {
    const response = await axios.get<SystemHealthResponse>(
      SYSTEM_HEALTH_ENDPOINTS.health,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          time_range: timeRange
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get error logs
 */
export async function getErrorLogs(
  timeRange: '24h' | '7d' | '30d',
  token: string,
  level?: 'error' | 'warning' | 'critical' | 'info',
  limit?: number
): Promise<ErrorLog[]> {
  try {
    const response = await axios.get<ErrorLogsResponse>(
      SYSTEM_HEALTH_ENDPOINTS.errors,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          time_range: timeRange,
          level,
          limit
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get error time series data
 */
export async function getErrorTimeSeries(
  timeRange: '24h' | '7d' | '30d',
  token: string
): Promise<TimeSeriesData[]> {
  try {
    const response = await axios.get<TimeSeriesResponse>(
      SYSTEM_HEALTH_ENDPOINTS.errorsTimeSeries,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          time_range: timeRange
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get login time series data
 */
export async function getLoginTimeSeries(
  timeRange: '24h' | '7d' | '30d',
  token: string
): Promise<TimeSeriesData[]> {
  try {
    const response = await axios.get<TimeSeriesResponse>(
      SYSTEM_HEALTH_ENDPOINTS.loginsTimeSeries,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          time_range: timeRange
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

export default {
  getSystemHealth,
  getErrorLogs,
  getErrorTimeSeries,
  getLoginTimeSeries
};
